import { format, isValid, parse } from "date-fns";

export const FLEXIBLE_PAYLOAD_VERSION = 3;
export const FLEXIBLE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const DISPLAY_DATE_FORMAT = "MMM d, yyyy";
const API_DATE_FORMAT = "yyyy-MM-dd";
const ADJUSTMENTS = ["available_from", "available_until"];
const HOUR_MS = 60 * 60 * 1000;

export const toHour = (time) => {
  if (time === null || time === undefined || time === "") return null;
  const match = /^(\d{1,2})(?::00)?$/.exec(String(time).trim());
  if (!match) return null;
  const hour = Number(match[1]);
  return hour >= 0 && hour <= 23 ? hour : null;
};

export const toTimeValue = (hour) => `${hour}:00`;

const parseDisplayDate = (date) => {
  const parsed = parse(date || "", DISPLAY_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

export const displayDateFromApi = (apiDate) => {
  const parsed = parse(apiDate || "", API_DATE_FORMAT, new Date());
  return isValid(parsed) ? format(parsed, DISPLAY_DATE_FORMAT) : null;
};

export const apiDateFromDisplay = (date) => {
  const parsed = parseDisplayDate(date);
  return parsed ? format(parsed, API_DATE_FORMAT) : null;
};

/*******************
@purpose : location wall-clock time as a comparable number of hours. Built
           with Date.UTC from the calendar parts, so differences are plain
           wall-clock hours whatever the browser's timezone or DST.
******************/
export const wallClockHours = (date, time) => {
  const day = parseDisplayDate(date);
  const hour = toHour(time);
  if (!day || hour === null) return null;
  return Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), hour) / HOUR_MS;
};

export const normalizeRange = (range) => {
  if (!range || typeof range !== "object") return null;
  const startDay = parseDisplayDate(range.startDate);
  const endDay = parseDisplayDate(range.endDate);
  const startHour = toHour(range.startTime);
  const endHour = toHour(range.endTime);
  if (!startDay || !endDay || startHour === null || endHour === null) return null;

  const normalized = {
    startDate: format(startDay, DISPLAY_DATE_FORMAT),
    endDate: format(endDay, DISPLAY_DATE_FORMAT),
    startTime: toTimeValue(startHour),
    endTime: toTimeValue(endHour),
  };
  const start = wallClockHours(normalized.startDate, normalized.startTime);
  const end = wallClockHours(normalized.endDate, normalized.endTime);
  return end > start ? normalized : null;
};

export const isSameRange = (a, b) =>
  Boolean(a && b) &&
  a.startDate === b.startDate &&
  a.endDate === b.endDate &&
  toHour(a.startTime) === toHour(b.startTime) &&
  toHour(a.endTime) === toHour(b.endTime);

export const adjustedRangeFromApi = (flexibleAvailability) =>
  normalizeRange({
    startDate: displayDateFromApi(flexibleAvailability?.start_date),
    endDate: displayDateFromApi(flexibleAvailability?.end_date),
    startTime: flexibleAvailability?.start_time,
    endTime: flexibleAvailability?.end_time,
  });

/*******************
@purpose : hours between the originally requested endpoint and the moved one,
           or null when the range breaks the one-endpoint rule. Exactly
           24-hour requests may move both endpoints later by the same amount.
******************/
export const flexibleShiftHours = (adjustment, requested, adjusted) => {
  if (!requested || !adjusted) return null;
  const requestedStart = wallClockHours(requested.startDate, requested.startTime);
  const requestedEnd = wallClockHours(requested.endDate, requested.endTime);
  const adjustedStart = wallClockHours(adjusted.startDate, adjusted.startTime);
  const adjustedEnd = wallClockHours(adjusted.endDate, adjusted.endTime);
  if ([requestedStart, requestedEnd, adjustedStart, adjustedEnd].some((value) => value === null)) return null;
  if (adjustedEnd - adjustedStart < 24) return null;

  if (requestedEnd - requestedStart === 24) {
    const shift = adjustedStart - requestedStart;
    return adjustment === "available_from" && adjustedEnd - requestedEnd === shift ? shift : null;
  }
  if (adjustment === "available_from") {
    if (requested.endDate !== adjusted.endDate || requested.endTime !== adjusted.endTime) return null;
    return (
      wallClockHours(adjusted.startDate, adjusted.startTime) -
      wallClockHours(requested.startDate, requested.startTime)
    );
  }
  if (adjustment === "available_until") {
    if (requested.startDate !== adjusted.startDate || requested.startTime !== adjusted.startTime) return null;
    return (
      wallClockHours(requested.endDate, requested.endTime) -
      wallClockHours(adjusted.endDate, adjusted.endTime)
    );
  }
  return null;
};

/*******************
@purpose : a payload is usable only when exactly the declared endpoint moved,
           in the declared direction, by at least an hour and never by more
           than the window the API evaluated it against. A 24-hour request
           instead preserves its duration by moving both endpoints later.
******************/
export const validateFlexiblePayload = (payload, now = Date.now()) => {
  if (!payload || payload.version !== FLEXIBLE_PAYLOAD_VERSION) return null;
  if (!ADJUSTMENTS.includes(payload.adjustment)) return null;
  if (!payload.vehicleSlug || typeof payload.vehicleSlug !== "string") return null;
  if (!Number.isFinite(payload.savedAt) || now - payload.savedAt > FLEXIBLE_MAX_AGE_MS) return null;
  if (!Number.isInteger(payload.windowHours) || payload.windowHours <= 0) return null;

  const requested = normalizeRange(payload.requested);
  const adjusted = normalizeRange(payload.adjusted);
  const shiftHours = flexibleShiftHours(payload.adjustment, requested, adjusted);
  if (!Number.isInteger(shiftHours) || shiftHours < 1 || shiftHours > payload.windowHours) return null;
  if (Number.isInteger(payload.shiftHours) && payload.shiftHours !== shiftHours) return null;

  return { ...payload, requested, adjusted, shiftHours };
};

export const buildFlexiblePayload = (vehicle, requestedRange, now = Date.now()) => {
  const flexible = vehicle?.flexible_availability;
  return validateFlexiblePayload(
    {
      version: FLEXIBLE_PAYLOAD_VERSION,
      vehicleSlug: vehicle?.slug,
      adjustment: flexible?.adjustment,
      windowHours: flexible?.window_hours,
      shiftHours: flexible?.shift_hours,
      requested: requestedRange,
      adjusted: adjustedRangeFromApi(flexible),
      savedAt: now,
    },
    now
  );
};

/*******************
@purpose : keep the moved endpoint inside the flexible offer's boundary,
           even when the slot API reports additional available hours.
******************/
export const isWithinFlexibleBoundary = (date, time, flexibleAvailability, endpoint) => {
  if (!flexibleAvailability) return true;
  const { adjustment, adjusted } = flexibleAvailability;
  const isStartBoundary = endpoint === "start" && adjustment === "available_from";
  const isEndBoundary = endpoint === "end" && adjustment === "available_until";
  if (!isStartBoundary && !isEndBoundary) return true;

  const candidate = wallClockHours(date, time);
  const boundary = isStartBoundary
    ? wallClockHours(adjusted?.startDate, adjusted?.startTime)
    : wallClockHours(adjusted?.endDate, adjusted?.endTime);
  if (candidate === null || boundary === null) return false;
  return isStartBoundary ? candidate >= boundary : candidate <= boundary;
};

/*******************
@purpose : the hours the vehicle page's time pickers offer for a date - same
           filter TimePicker renders (slot has a free unit, after the booking
           lead time, after the pickup hour when both are the same day,
           and inside an optional flexible offer's endpoint boundary)
@Parameter : {date, availableDates, minBookableHour, afterHour, flexibleAvailability, endpoint}
******************/
export const selectableHours = (date, availableDates, {
  minBookableHour = -Infinity,
  afterHour = null,
  flexibleAvailability = null,
  endpoint = null,
} = {}) => {
  const apiDate = apiDateFromDisplay(date);
  const daySlots = apiDate && availableDates ? availableDates[apiDate] : null;
  if (!daySlots) return [];

  return Object.entries(daySlots)
    .filter(([hour, count]) => {
      const hourNum = Number(hour);
      return (
        count !== 0 &&
        hourNum > minBookableHour &&
        (afterHour === null || hourNum > afterHour) &&
        isWithinFlexibleBoundary(date, toTimeValue(hourNum), flexibleAvailability, endpoint)
      );
    })
    .map(([hour]) => toTimeValue(Number(hour)));
};

/*******************
@purpose : whether both ends of a range can be picked on the vehicle page as is
@Parameter : {range, availableDates, minBookableHourFor(date), flexibleAvailability}
******************/
export const isRangeSelectable = (range, availableDates, minBookableHourFor = () => -Infinity, flexibleAvailability = null) => {
  const normalized = normalizeRange(range);
  if (!normalized) return false;

  const startOptions = selectableHours(normalized.startDate, availableDates, {
    minBookableHour: minBookableHourFor(normalized.startDate),
    flexibleAvailability,
    endpoint: "start",
  });
  const endOptions = selectableHours(normalized.endDate, availableDates, {
    minBookableHour: minBookableHourFor(normalized.endDate),
    afterHour: normalized.startDate === normalized.endDate ? toHour(normalized.startTime) : null,
    flexibleAvailability,
    endpoint: "end",
  });

  return startOptions.includes(normalized.startTime) && endOptions.includes(normalized.endTime);
};

/*******************
@purpose : what a time select should show. An unavailable value is never shown
           as if it were the first option - the select shows a placeholder and
           the caller must clear the value so state and pricing agree with it.
******************/
export const resolveTimeSelection = (value, options) => {
  if (!value) return { selectValue: "", isUnavailable: false };
  const normalized = toHour(value) === null ? null : toTimeValue(toHour(value));
  const available = normalized !== null && options.includes(normalized);
  if (available) return { selectValue: normalized, isUnavailable: false };
  return { selectValue: "", isUnavailable: options.length > 0 };
};

export const formatFlexibleAdjustment = (payload) => {
  if (!payload) return null;
  const isFrom = payload.adjustment === "available_from";
  const movedDate = isFrom ? payload.adjusted.startDate : payload.adjusted.endDate;
  const movedTime = isFrom ? payload.adjusted.startTime : payload.adjusted.endTime;
  const originalDate = isFrom ? payload.requested.startDate : payload.requested.endDate;
  const day = parseDisplayDate(movedDate);
  const hour = toHour(movedTime);
  if (!day || hour === null) return null;

  const moved = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour);
  return {
    label: isFrom ? "Available from" : "Available until",
    value: movedDate === originalDate ? format(moved, "h:mm a") : format(moved, "EEE, MMM d"),
  };
};

export const formatRangeEndpoint = (date, time) => {
  const day = parseDisplayDate(date);
  const hour = toHour(time);
  if (!day || hour === null) return "";
  return format(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour), "EEE, MMM d · h:mm a");
};
